import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button,
  Grid, Skeleton, Divider,
} from '@mui/material';
import {
  TrendingUp, ArrowForward, ArrowUpward, ArrowDownward,
  TrendingFlat,
} from '@mui/icons-material';
import {
  Repository,
  useGetScanHistoryQuery,
  useGetScanQuery,
  useCompareScansQuery,
} from '../../store/api/githubApi';
import { ScanComparisonDrawer } from '../scans/ScanComparisonDrawer';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Smooth Spline Curve for Sparkline ─────────────────────────────────────────

function getCubicBezierPath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }
  let path = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? i : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return path;
}

// ── Compact Sparkline Component ───────────────────────────────────────────────

interface SparklinePoint {
  id: number;
  score: number;
  label: string;
  date: string;
}

const MinimalSparkline: React.FC<{ scans: SparklinePoint[] }> = ({ scans }) => {
  if (scans.length < 2) {
    return (
      <Box
        sx={{
          py: 2.5,
          px: 2,
          textAlign: 'center',
          bgcolor: '#f8fafc',
          borderRadius: '12px',
          border: '1px dashed #cbd5e1',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="body2" fontWeight={600} color="#64748b">
          Insufficient historical scan data. Run at least 2 scans to view health trends.
        </Typography>
      </Box>
    );
  }

  const svgWidth = 540;
  const svgHeight = 90;
  const padX = 35;
  const padTop = 22;
  const padBottom = 22;

  const scores = scans.map((s) => s.score);
  const rawMin = Math.min(...scores);
  const rawMax = Math.max(...scores);
  const minScore = Math.max(0, rawMin - 6);
  const maxScore = Math.min(100, rawMax + 6);
  const scoreRange = Math.max(8, maxScore - minScore);

  const points = scans.map((s, idx) => {
    const x = padX + (idx / (scans.length - 1)) * (svgWidth - 2 * padX);
    const y = padTop + (1 - (s.score - minScore) / scoreRange) * (svgHeight - padTop - padBottom);
    return {
      x,
      y,
      score: s.score,
      label: s.label,
      date: s.date,
      isLast: idx === scans.length - 1,
    };
  });

  const curvePath = getCubicBezierPath(points);
  const areaPath = `${curvePath} L ${points[points.length - 1].x},${svgHeight - padBottom + 8} L ${points[0].x},${svgHeight - padBottom + 8} Z`;

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        p: { xs: 1, sm: 1.5 },
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 110 }}
      >
        <defs>
          <linearGradient id="healthTrendGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Subtle Horizontal Reference Grid Lines */}
        <line
          x1={padX - 10}
          y1={padTop + 4}
          x2={svgWidth - padX + 10}
          y2={padTop + 4}
          stroke="#e2e8f0"
          strokeDasharray="3 3"
          strokeWidth="1"
        />
        <line
          x1={padX - 10}
          y1={svgHeight / 2}
          x2={svgWidth - padX + 10}
          y2={svgHeight / 2}
          stroke="#e2e8f0"
          strokeDasharray="3 3"
          strokeWidth="1"
        />
        <line
          x1={padX - 10}
          y1={svgHeight - padBottom}
          x2={svgWidth - padX + 10}
          y2={svgHeight - padBottom}
          stroke="#e2e8f0"
          strokeDasharray="3 3"
          strokeWidth="1"
        />

        {/* Area Glow under Curve */}
        <path d={areaPath} fill="url(#healthTrendGradient)" />

        {/* Primary Trend Line */}
        <path
          d={curvePath}
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Connecting Data Points with Score Badges */}
        {points.map((pt, idx) => (
          <g key={idx}>
            {/* Score Text above Point */}
            <text
              x={pt.x}
              y={pt.y - 8}
              textAnchor="middle"
              fill={pt.isLast ? '#2563eb' : '#0f172a'}
              fontSize="11"
              fontWeight={pt.isLast ? '800' : '700'}
              fontFamily='"JetBrains Mono", monospace'
            >
              {pt.score}
            </text>

            {/* Target Dot */}
            {pt.isLast ? (
              <>
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r="7.5"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                  opacity="0.3"
                />
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r="4.5"
                  fill="#2563eb"
                  stroke="#ffffff"
                  strokeWidth="2"
                />
              </>
            ) : (
              <circle
                cx={pt.x}
                cy={pt.y}
                r="3.5"
                fill="#ffffff"
                stroke="#2563eb"
                strokeWidth="2"
              />
            )}

            {/* Scan Label below Point */}
            <text
              x={pt.x}
              y={svgHeight - 4}
              textAnchor="middle"
              fill="#64748b"
              fontSize="9.5"
              fontWeight="600"
            >
              {pt.label}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
};

// ── Repository Row Subcomponent (Requirement 6) ───────────────────────────────

const RepoTrendRow: React.FC<{
  repo: Repository;
  isActive: boolean;
  onSelect: () => void;
}> = ({ repo, isActive, onSelect }) => {
  const { data: history = [] } = useGetScanHistoryQuery(repo.id);
  const current = history[0];
  const prev = history[1];

  const currentScore = Math.round(current?.debt_score ?? repo.latest_debt_score ?? 0);
  const prevScore = prev?.debt_score != null ? Math.round(prev.debt_score) : null;
  const delta = prevScore != null ? currentScore - prevScore : null;

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.6,
        px: 1,
        borderRadius: '8px',
        bgcolor: isActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
        border: isActive ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid transparent',
        cursor: 'pointer',
        transition: 'all 150ms ease',
        '&:hover': {
          bgcolor: isActive ? 'rgba(37, 99, 235, 0.1)' : '#f1f5f9',
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={isActive ? 800 : 600}
        sx={{
          color: isActive ? '#1d4ed8' : '#334155',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          maxWidth: 150,
        }}
      >
        {repo.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography
          variant="body2"
          fontWeight={800}
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.82rem',
            color: '#0f172a',
          }}
        >
          {currentScore}
        </Typography>

        {delta !== null ? (
          delta > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#16a34a' }}>
              <ArrowUpward sx={{ fontSize: 13 }} />
              <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.72rem' }}>
                {delta}
              </Typography>
            </Box>
          ) : delta < 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#dc2626' }}>
              <ArrowDownward sx={{ fontSize: 13 }} />
              <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.72rem' }}>
                {Math.abs(delta)}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
              <TrendingFlat sx={{ fontSize: 13 }} />
            </Box>
          )
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
            <TrendingFlat sx={{ fontSize: 13 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Main HealthTrendCard Component ────────────────────────────────────────────

export interface HealthTrendCardProps {
  repos: Repository[];
}

export const HealthTrendCard: React.FC<HealthTrendCardProps> = ({ repos }) => {
  const scannedRepos = (repos ?? []).filter((r) => r.latest_debt_score !== null);

  // Active repository selection (defaults to most recently scanned repository)
  const defaultRepo = scannedRepos.slice().sort((a, b) => {
    const timeA = a.latest_scan_at ? new Date(a.latest_scan_at).getTime() : 0;
    const timeB = b.latest_scan_at ? new Date(b.latest_scan_at).getTime() : 0;
    return timeB - timeA;
  })[0] ?? scannedRepos[0];

  const [selectedRepoId, setSelectedRepoId] = useState<number | null>(null);
  const activeRepo = scannedRepos.find((r) => r.id === selectedRepoId) ?? defaultRepo;

  // Comparison drawer state (Requirement 7)
  const [comparisonDrawerOpen, setComparisonDrawerOpen] = useState(false);

  // Existing RTK Query hooks for active repo scan history & scan details (NO new APIs)
  const { data: history = [], isLoading: historyLoading } = useGetScanHistoryQuery(
    activeRepo?.id ?? 0,
    { skip: !activeRepo?.id }
  );

  const { data: currentScanData } = useGetScanQuery(
    activeRepo?.latest_scan_id ?? 0,
    { skip: !activeRepo?.latest_scan_id }
  );

  // Current & Previous scans from history
  const currentScan = history[0];
  const prevScan = history[1];

  // Real counts from Issue.fingerprint set-diffing (see app/api/scans.py's
  // /{scan_id}/compare/{baseline_id}) — this card previously fell back to hardcoded
  // 42/11/6/1,421 whenever currentIssues didn't produce a non-zero count via a guessed formula.
  // See docs/scan-report-integrity-plan.md, KG-02.
  const { data: comparison } = useCompareScansQuery(
    { scanId: currentScan?.id, baselineId: prevScan?.id },
    { skip: !currentScan?.id || !prevScan?.id }
  );

  if (!activeRepo) return null;

  // History scans ordered oldest to newest for the sparkline (up to last 5 scans)
  const lastScans = history.slice(0, 5).reverse();
  const sparklineData: SparklinePoint[] = lastScans.map((s, idx) => ({
    id: s.id,
    score: Math.round(s.debt_score ?? 0),
    label: `Scan #${s.id}`,
    date: fmtDate(s.created_at),
  }));

  const currentScore = Math.round(currentScan?.debt_score ?? activeRepo.latest_debt_score ?? 0);
  const prevScore = prevScan?.debt_score != null ? Math.round(prevScan.debt_score) : null;
  const scoreDelta = prevScore != null ? currentScore - prevScore : null;

  const currentIssues = currentScanData?.issues ?? [];
  const resolvedCount = comparison?.resolved_count ?? null;
  const newCount = comparison?.new_count ?? null;
  const changedCount = comparison?.changed_count ?? null;
  const unchangedCount = comparison?.unchanged_count ?? null;

  return (
    <>
      <Card
        elevation={0}
        sx={{
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)',
          overflow: 'hidden',
          transition: 'transform 180ms ease, box-shadow 180ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.08)',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 }, '&:last-child': { pb: { xs: 2.5, sm: 3 } } }}>
          {/* 1. Header: Title & Last X scans badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: 'rgba(37, 99, 235, 0.08)',
                  color: '#2563eb',
                }}
              >
                <TrendingUp sx={{ fontSize: 19 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.2, fontSize: '1.05rem' }}>
                  Health Trend
                </Typography>
                {scannedRepos.length > 1 && (
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
                    Tracking: <span style={{ color: '#2563eb', fontWeight: 700 }}>{activeRepo.name}</span>
                  </Typography>
                )}
              </Box>
            </Box>

            <Chip
              label={sparklineData.length > 1 ? `Last ${sparklineData.length} scans` : 'Recent scan'}
              size="small"
              sx={{
                bgcolor: '#f1f5f9',
                color: '#475569',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            />
          </Box>

          {/* 2. Minimal Trend Visualization (Requirement 3) */}
          {historyLoading ? (
            <Skeleton variant="rounded" width="100%" height={90} sx={{ bgcolor: '#f1f5f9', borderRadius: '12px', mb: 2.5 }} />
          ) : (
            <Box sx={{ mb: 2.5 }}>
              <MinimalSparkline scans={sparklineData} />
            </Box>
          )}

          {/* 3. Metrics & Delta Bar (Requirements 4, 5, 6, 7) */}
          <Grid container spacing={2} alignItems="center">
            {/* Current vs Previous Score (Requirement 4) */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                    Current Score
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={900}
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      color: '#0f172a',
                      lineHeight: 1.1,
                      mt: 0.25,
                    }}
                  >
                    {currentScore}
                  </Typography>
                </Box>

                {scoreDelta !== null ? (
                  scoreDelta > 0 ? (
                    <Chip
                      icon={<ArrowUpward sx={{ '&&': { color: '#16a34a', fontSize: 14 } }} />}
                      label={`+${scoreDelta} since previous scan`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(34, 197, 94, 0.08)',
                        color: '#16a34a',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        border: '1px solid rgba(34, 197, 94, 0.25)',
                        borderRadius: '8px',
                      }}
                    />
                  ) : scoreDelta < 0 ? (
                    <Chip
                      icon={<ArrowDownward sx={{ '&&': { color: '#dc2626', fontSize: 14 } }} />}
                      label={`${scoreDelta} since previous scan`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(239, 68, 68, 0.08)',
                        color: '#dc2626',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: '8px',
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<TrendingFlat sx={{ '&&': { color: '#64748b', fontSize: 14 } }} />}
                      label="0 since previous scan"
                      size="small"
                      sx={{
                        bgcolor: '#f1f5f9',
                        color: '#64748b',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                      }}
                    />
                  )
                ) : (
                  <Chip
                    label="Baseline scan"
                    size="small"
                    sx={{
                      bgcolor: '#f1f5f9',
                      color: '#64748b',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      borderRadius: '8px',
                    }}
                  />
                )}
              </Box>
            </Grid>

            {/* Finding Changes Summary (Requirement 5) */}
            <Grid item xs={12} sm={6} md={scannedRepos.length > 1 ? 5 : 5.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  flexWrap: 'wrap',
                  p: 1.25,
                  bgcolor: '#f8fafc',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                }}
              >
                {comparison ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={800} color="#16a34a" sx={{ fontSize: '0.85rem' }}>
                        {resolvedCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        resolved
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto', borderColor: '#cbd5e1' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={800} color="#f59e0b" sx={{ fontSize: '0.85rem' }}>
                        {newCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        new findings
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto', borderColor: '#cbd5e1' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={800} color="#2563eb" sx={{ fontSize: '0.85rem' }}>
                        {changedCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        changed
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto', borderColor: '#cbd5e1' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={800} color="#64748b" sx={{ fontSize: '0.85rem' }}>
                        {unchangedCount?.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        unchanged
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {prevScan ? 'Comparing scans…' : 'No prior scan to compare against yet.'}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* View Comparison Action (Requirement 7) */}
            <Grid item xs={12} sm={12} md={scannedRepos.length > 1 ? 3.5 : 3}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                <Button
                  onClick={() => setComparisonDrawerOpen(true)}
                  disabled={!prevScan && history.length < 2}
                  endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    color: '#2563eb',
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.75,
                    bgcolor: 'rgba(37, 99, 235, 0.05)',
                    border: '1px solid rgba(37, 99, 235, 0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(37, 99, 235, 0.12)',
                      borderColor: '#2563eb',
                    },
                    '&.Mui-disabled': {
                      color: '#94a3b8',
                      borderColor: '#e2e8f0',
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  View comparison
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* 4. Repository-Level Trend (Requirement 6) */}
          {scannedRepos.length > 1 && (
            <Box sx={{ mt: 2.25, pt: 2, borderTop: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.7rem' }}
                >
                  Repository Health
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Click repository to inspect trend
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {scannedRepos.slice(0, 3).map((r) => (
                  <RepoTrendRow
                    key={r.id}
                    repo={r}
                    isActive={r.id === activeRepo.id}
                    onSelect={() => setSelectedRepoId(r.id)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 5. Existing Scan Comparison Drawer (Requirement 7) */}
      {currentScan && (
        <ScanComparisonDrawer
          open={comparisonDrawerOpen}
          onClose={() => setComparisonDrawerOpen(false)}
          currentScan={currentScan}
          prevScan={prevScan ?? currentScan}
          currentIssues={currentIssues}
        />
      )}
    </>
  );
};
